import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, HelpCircle } from 'lucide-react';



export function ContactPage() {
    const location = useLocation();
    const navigate = useNavigate();

    // Pre-fill state from navigation (e.g. from ImagePage)
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [email, setEmail] = useState('');
    const [category, setCategory] = useState<'content' | 'other'>('content');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (location.state) {
            if (location.state.subject) setSubject(location.state.subject);
            if (location.state.description) setDescription(location.state.description);
            // Clean up state so refreshing doesn't keep pre-filling if user navigates away and back
            // navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));

        setIsSubmitting(false);
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
                    <Send className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white mb-2">
                    Mesajınız Alındı
                </h2>
                <p className="text-gray-600 dark:text-gray-400 max-w-md">
                    Geri bildiriminiz için teşekkür ederiz. İncelemelerimiz tamamlandıktan sonra e-posta adresiniz üzerinden size dönüş yapılacaktır.
                </p>
                <button
                    onClick={() => navigate('/')}
                    className="mt-8 px-6 py-2.5 bg-mavi-500 hover:bg-mavi-600 text-white rounded-xl font-medium transition-colors"
                >
                    Ana Sayfaya Dön
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-poppins font-bold text-gray-900 dark:text-white mb-2">
                    İletişim & Destek
                </h1>
                <div className="h-1 w-20 bg-mavi-500 rounded-full"></div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-4">
                    <InfoBox />
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 bg-white dark:bg-surface-900 p-8 rounded-2xl border border-surface-200 dark:border-surface-700 shadow-sm">

                {/* Email */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-poppins">
                        E-posta Adresiniz
                    </label>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 focus:outline-none focus:ring-2 focus:ring-mavi-500/20 focus:border-mavi-500 transition-all text-gray-900 dark:text-white"
                        placeholder="ornek@email.com"
                    />
                </div>

                {/* Category */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 font-poppins">
                        Bildirim Kategorisi
                    </label>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <label className="flex items-center gap-3 p-3 rounded-xl border border-surface-200 dark:border-surface-700 cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                            <input
                                type="radio"
                                name="category"
                                checked={category === 'content'}
                                onChange={() => setCategory('content')}
                                className="w-4 h-4 text-mavi-500 focus:ring-mavi-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Sözlük İçeriği / Şikayet</span>
                        </label>
                        <label className="flex items-center gap-3 p-3 rounded-xl border border-surface-200 dark:border-surface-700 cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                            <input
                                type="radio"
                                name="category"
                                checked={category === 'other'}
                                onChange={() => setCategory('other')}
                                className="w-4 h-4 text-mavi-500 focus:ring-mavi-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Reklam ve Diğer Konular</span>
                        </label>
                    </div>
                </div>

                {/* Subject */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-poppins">
                        Konu
                    </label>
                    <input
                        type="text"
                        required
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 focus:outline-none focus:ring-2 focus:ring-mavi-500/20 focus:border-mavi-500 transition-all text-gray-900 dark:text-white font-medium"
                        placeholder="Örn: Hatalı içerik bildirimi"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-poppins">
                        Mesajınız
                    </label>
                    <textarea
                        required
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={6}
                        className="w-full px-4 py-3 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 focus:outline-none focus:ring-2 focus:ring-mavi-500/20 focus:border-mavi-500 transition-all text-gray-900 dark:text-white resize-none"
                        placeholder="Mesajınızı detaylı bir şekilde açıklayınız..."
                    />
                </div>

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-gradient-to-r from-mavi-600 to-mavi-500 hover:from-mavi-500 hover:to-mavi-400 text-white rounded-xl font-bold font-poppins shadow-lg shadow-mavi-500/20 transition-all transform active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <>
                            <Send className="w-5 h-5" />
                            Gönder
                        </>
                    )}
                </button>

            </form>
        </div>
    );
}

function InfoBox() {
    return (
        <>
            <HelpCircle className="w-6 h-6 text-mavi-500 shrink-0 mt-1" />
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                <p className="font-semibold text-gray-900 dark:text-white">
                    Dikkat Edilmesi Gerekenler:
                </p>
                <ul className="list-disc pl-4 space-y-2">
                    <li>
                        Sorularınız için öncelikle <a href="/faq" className="text-mavi-500 hover:underline">Sıkça Sorulan Sorular</a> sayfasını incelemenizi öneririz.
                    </li>
                    <li>
                        Gönderdiğiniz mesajlar <strong>doğrudan yazarlara iletilmez</strong>. Yazarlar, içeriklerinden kendileri sorumludur.
                    </li>
                    <li>
                        Hukuki bildirimler ve telif hakkı ihlalleri, yalnızca hak sahibi veya yetkili vekili tarafından yapıldığında işleme alınmaktadır.
                    </li>
                    <li>
                        Üçüncü şahıslar adına yapılan şikayetler, yasal zorunluluk olmadıkça değerlendirmeye alınmayabilir.
                    </li>
                </ul>
            </div>
        </>
    );
}
